import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { GearSix, ShieldCheck, Trash, Key } from "@phosphor-icons/react";
import WorkflowBuilder from "./admin/WorkflowBuilder";
import SystemSettings from "./admin/SystemSettings";
import Broadcast from "./admin/Broadcast";
import BulkTasks from "./admin/BulkTasks";
import Plugins from "./admin/Plugins";
import RewardCatalogAdmin from "./admin/RewardCatalogAdmin";
import InviteAndReplenish from "./admin/InviteAndReplenish";
import RegisteredUsers from "./admin/RegisteredUsers";
import AIAgents from "./admin/AIAgents";

export default function Admin() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [depts, setDepts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [health, setHealth] = useState(null);
  const [newDept, setNewDept] = useState({ name: "", monthly_credit_allocation: 5000 });
  const [grant, setGrant] = useState({ department_id: "", amount: 1000, notes: "Manual grant" });
  const [pwReset, setPwReset] = useState({ user_id: null, password: "" });
  const [workload, setWorkload] = useState({ user_id: null, pct: 50 });

  const load = async () => {
    const [u, d, l, h] = await Promise.all([
      api.get("/users"), api.get("/departments"),
      api.get("/audit-logs").catch(() => ({ data: [] })),
      api.get("/admin/system-health").catch(() => ({ data: null })),
    ]);
    setUsers(u.data); setDepts(d.data); setLogs(l.data); setHealth(h.data);
  };
  useEffect(() => { load(); }, []);

  const changeRole = async (uid, role) => {
    try {
      await api.patch(`/users/${uid}/role`, { role });
      toast.success("Role updated");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const createDept = async () => {
    if (!newDept.name.trim()) return toast.error("Department name required");
    try {
      await api.post("/admin/departments", newDept);
      toast.success("Department created");
      setNewDept({ name: "", monthly_credit_allocation: 5000 });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const grantCredits = async () => {
    if (!grant.department_id) return toast.error("Select a department");
    try {
      await api.post("/admin/credit-grant", grant);
      toast.success("Credits granted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const resetPassword = async () => {
    if (!pwReset.password || pwReset.password.length < 6) return toast.error("Password must be 6+ chars");
    try {
      await api.post(`/admin/users/${pwReset.user_id}/reset-password`, { password: pwReset.password });
      toast.success("Password reset");
      setPwReset({ user_id: null, password: "" });
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const setUserWorkload = async () => {
    try {
      await api.post(`/admin/users/${workload.user_id}/workload`, { workload_pct: workload.pct });
      toast.success("Workload updated");
      setWorkload({ user_id: null, pct: 50 });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const deleteUser = async (uid) => {
    if (!window.confirm("Delete this user? This is permanent.")) return;
    try {
      await api.delete(`/admin/users/${uid}`);
      toast.success("User deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  if (user.role !== "ADMIN") {
    return <div className="p-8 text-sm text-zinc-500">Admins only.</div>;
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto" data-testid="admin-page">
      <div className="mb-6 flex items-center gap-3">
        <GearSix size={28} />
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Admin</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-bold">Console</h1>
        </div>
      </div>

      {/* System health strip */}
      {health && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2 mb-6" data-testid="system-health-strip">
          {[
            ["users", "Users"], ["tasks", "Tasks"], ["applications", "Apps"],
            ["notifications", "Notifs"], ["credit_transactions", "Ledger"],
            ["audit_logs", "Audits"], ["workflows", "Flows"], ["departments", "Depts"],
          ].map(([k, l]) => (
            <div key={k} className="border border-border p-3 bg-background" data-testid={`health-${k}`}>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{l}</div>
              <div className="text-lg font-bold font-mono">{health[k]}</div>
            </div>
          ))}
        </div>
      )}

      <Tabs defaultValue="users" className="w-full">
        <div className="overflow-x-auto -mx-2 px-2 pb-1">
          <TabsList className="rounded-sm flex-nowrap h-auto w-max">
            <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
            <TabsTrigger value="registered" data-testid="admin-tab-registered">Registered Users</TabsTrigger>
            <TabsTrigger value="departments" data-testid="admin-tab-departments">Departments</TabsTrigger>
            <TabsTrigger value="economy" data-testid="admin-tab-economy">Credits</TabsTrigger>
            <TabsTrigger value="workflows" data-testid="admin-tab-workflows">Workflows</TabsTrigger>
            <TabsTrigger value="plugins" data-testid="admin-tab-plugins">Plugins</TabsTrigger>
            <TabsTrigger value="ai_agents" data-testid="admin-tab-ai-agents">AI Agents</TabsTrigger>
            <TabsTrigger value="rewards" data-testid="admin-tab-rewards">Rewards</TabsTrigger>
            <TabsTrigger value="ops" data-testid="admin-tab-ops">Invite & Replenish</TabsTrigger>
            <TabsTrigger value="settings" data-testid="admin-tab-settings">Settings</TabsTrigger>
            <TabsTrigger value="broadcast" data-testid="admin-tab-broadcast">Broadcast</TabsTrigger>
            <TabsTrigger value="bulk" data-testid="admin-tab-bulk">Bulk tasks</TabsTrigger>
            <TabsTrigger value="audit" data-testid="admin-tab-audit">Audit log</TabsTrigger>
          </TabsList>
        </div>

        {/* Users */}
        <TabsContent value="users" className="pt-4">
          <div className="border border-border bg-background overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead className="border-b border-border">
                <tr className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  <th className="text-left py-2 px-3">Name</th>
                  <th className="text-left py-2 px-3 hidden md:table-cell">Email</th>
                  <th className="text-left py-2 px-3">Role</th>
                  <th className="text-right py-2 px-3 hidden md:table-cell">Workload</th>
                  <th className="text-right py-2 px-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-b border-border" data-testid={`user-row-${u.user_id}`}>
                    <td className="py-2 px-3 font-medium">{u.name}</td>
                    <td className="py-2 px-3 text-zinc-500 hidden md:table-cell">{u.email}</td>
                    <td className="py-2 px-3">
                      <Select value={u.role} onValueChange={(v) => changeRole(u.user_id, v)}>
                        <SelectTrigger className="rounded-sm h-8 w-[140px] text-xs" data-testid={`role-select-${u.user_id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="EMPLOYEE">EMPLOYEE</SelectItem>
                          <SelectItem value="TASK_OWNER">TASK_OWNER</SelectItem>
                          <SelectItem value="REVIEWER">REVIEWER</SelectItem>
                          <SelectItem value="MANAGER">MANAGER</SelectItem>
                          <SelectItem value="ADMIN">ADMIN</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-2 px-3 text-right font-mono hidden md:table-cell">{u.workload_pct}%</td>
                    <td className="py-2 px-3 text-right">
                      <div className="flex gap-1 justify-end">
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button size="sm" variant="outline" onClick={() => setWorkload({ user_id: u.user_id, pct: u.workload_pct })} className="rounded-sm h-7 px-2" data-testid={`workload-btn-${u.user_id}`}>
                              Load
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="rounded-sm max-w-xs">
                            <DialogHeader><DialogTitle>Set workload for {u.name}</DialogTitle></DialogHeader>
                            <Input type="number" min={0} max={100} value={workload.pct} onChange={(e) => setWorkload({ ...workload, pct: Number(e.target.value) })} className="rounded-sm" data-testid="workload-input" />
                            <DialogFooter>
                              <Button onClick={setUserWorkload} className="rounded-sm bg-foreground text-background" data-testid="workload-save">Save</Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button size="sm" variant="outline" onClick={() => setPwReset({ user_id: u.user_id, password: "" })} className="rounded-sm h-7 px-2" data-testid={`pwreset-btn-${u.user_id}`}>
                              <Key size={12} />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="rounded-sm max-w-xs">
                            <DialogHeader><DialogTitle>Reset password for {u.name}</DialogTitle></DialogHeader>
                            <Input type="password" value={pwReset.password} onChange={(e) => setPwReset({ ...pwReset, password: e.target.value })} placeholder="New password (6+ chars)" className="rounded-sm" data-testid="pwreset-input" />
                            <DialogFooter>
                              <Button onClick={resetPassword} className="rounded-sm bg-foreground text-background" data-testid="pwreset-save">Reset</Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                        <Button size="sm" variant="outline" onClick={() => deleteUser(u.user_id)} className="rounded-sm h-7 px-2 text-red-600 border-red-200" data-testid={`delete-user-${u.user_id}`}>
                          <Trash size={12} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Registered Users (read-only admin view) */}
        <TabsContent value="registered" className="pt-4">
          <RegisteredUsers />
        </TabsContent>

        {/* Departments */}
        <TabsContent value="departments" className="pt-4 space-y-4">
          <div className="border border-border p-4 bg-background grid grid-cols-1 md:grid-cols-4 gap-3 items-end" data-testid="create-dept-form">
            <div className="md:col-span-2 space-y-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">New department name</Label>
              <Input value={newDept.name} onChange={(e) => setNewDept({ ...newDept, name: e.target.value })} placeholder="Finance" className="rounded-sm" data-testid="new-dept-name" />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Monthly credits</Label>
              <Input type="number" value={newDept.monthly_credit_allocation} onChange={(e) => setNewDept({ ...newDept, monthly_credit_allocation: Number(e.target.value) })} className="rounded-sm" data-testid="new-dept-credits" />
            </div>
            <Button onClick={createDept} className="rounded-sm bg-foreground text-background" data-testid="create-dept-btn">Create</Button>
          </div>
          <div className="border border-border bg-background">
            <table className="w-full text-sm" data-testid="depts-table">
              <thead className="border-b border-border"><tr className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                <th className="text-left py-2 px-3">Department</th>
                <th className="text-right py-2 px-3">Monthly allocation</th>
              </tr></thead>
              <tbody>
                {depts.map((d) => (
                  <tr key={d.dept_id} className="border-b border-border">
                    <td className="py-2 px-3">{d.name}</td>
                    <td className="py-2 px-3 text-right font-mono text-[#E1252A]">{d.monthly_credit_allocation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Credits */}
        <TabsContent value="economy" className="pt-4">
          <div className="border border-border p-4 bg-background grid grid-cols-1 md:grid-cols-4 gap-3 items-end" data-testid="grant-form">
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Department</Label>
              <Select value={grant.department_id} onValueChange={(v) => setGrant({ ...grant, department_id: v })}>
                <SelectTrigger className="rounded-sm" data-testid="grant-dept-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {depts.map((d) => <SelectItem key={d.dept_id} value={d.dept_id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Amount</Label>
              <Input type="number" value={grant.amount} onChange={(e) => setGrant({ ...grant, amount: Number(e.target.value) })} className="rounded-sm" data-testid="grant-amount" />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Notes</Label>
              <Input value={grant.notes} onChange={(e) => setGrant({ ...grant, notes: e.target.value })} className="rounded-sm" data-testid="grant-notes" />
            </div>
            <Button onClick={grantCredits} className="rounded-sm bg-foreground text-background" data-testid="grant-btn">Grant credits</Button>
          </div>
        </TabsContent>

        {/* Workflows */}
        <TabsContent value="workflows" className="pt-4">
          <WorkflowBuilder />
        </TabsContent>

        {/* Plugins */}
        <TabsContent value="plugins" className="pt-4">
          <Plugins />
        </TabsContent>

        {/* Rewards catalog */}
        <TabsContent value="rewards" className="pt-4">
          <RewardCatalogAdmin />
        </TabsContent>

        {/* Ops: invite + replenish */}
        <TabsContent value="ops" className="pt-4">
          <InviteAndReplenish />
        </TabsContent>

        {/* Settings */}
        <TabsContent value="settings" className="pt-4">
          <SystemSettings />
        </TabsContent>

        {/* Broadcast */}
        <TabsContent value="broadcast" className="pt-4">
          <Broadcast />
        </TabsContent>

        {/* Bulk tasks */}
        <TabsContent value="bulk" className="pt-4">
          <BulkTasks />
        </TabsContent>

        {/* Audit */}
        <TabsContent value="audit" className="pt-4">
          <div className="border border-border bg-background overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead className="border-b border-border"><tr className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                <th className="text-left py-2 px-3">When</th>
                <th className="text-left py-2 px-3">Actor</th>
                <th className="text-left py-2 px-3">Action</th>
                <th className="text-left py-2 px-3">Target</th>
              </tr></thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.log_id} className="border-b border-border">
                    <td className="py-2 px-3 text-zinc-500 text-xs">{new Date(l.created_at).toLocaleString()}</td>
                    <td className="py-2 px-3 font-mono text-xs">{l.actor_id?.slice(0, 12)}</td>
                    <td className="py-2 px-3 font-mono text-xs">{l.action}</td>
                    <td className="py-2 px-3 text-xs">{l.target_type}:{String(l.target_id || "").slice(0, 12)}</td>
                  </tr>
                ))}
                {logs.length === 0 && <tr><td colSpan={4} className="text-center py-12 text-sm text-zinc-500">No activity yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="ai_agents" className="pt-4">
          <AIAgents />
        </TabsContent>
      </Tabs>
    </div>
  );
}
